import type * as React from "react";
import { Toast } from "@base-ui/react/toast";

export const toastManager = Toast.createToastManager();

/**
 * Toast variants (legacy parity: the old MUI `Alert` had success / info /
 * warning / error severities). `destructive` is the error variant — it keeps
 * that name because ~150 call sites already use it.
 */
export type ToastVariant = "default" | "success" | "info" | "warning" | "destructive";

interface ToastOptions {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  /** Makes the whole toast clickable (e.g. notification deep link); the toast closes after. */
  onClick?: () => void;
}

function toastType(variant: ToastVariant | undefined): string | undefined {
  return variant && variant !== "default" ? variant : undefined;
}

function toast(opts: ToastOptions) {
  const id = toastManager.add({
    title: opts.title,
    description: opts.description,
    type: toastType(opts.variant),
    data: opts.onClick ? { onClick: opts.onClick } : undefined,
  });

  return {
    id,
    dismiss: () => toastManager.close(id),
    update: (updates: ToastOptions) =>
      toastManager.update(id, {
        title: updates.title,
        description: updates.description,
        type: toastType(updates.variant),
      }),
  };
}

function useToast() {
  const manager = Toast.useToastManager();
  return {
    toasts: manager.toasts,
    toast,
    dismiss: (toastId?: string) => manager.close(toastId),
  };
}

/**
 * Reads the click action a toast was created with (the `onClick` option above).
 * The single place that knows the shape stored in Base UI's untyped `data`.
 */
function getToastAction(t: { data?: unknown }): (() => void) | undefined {
  return (t.data as { onClick?: () => void } | undefined)?.onClick;
}

export { useToast, toast, getToastAction };
